import { Breadcrumb, Button, Card, Dropdown, Flex, Row, Space } from "antd";
import { MainLayout } from "components/layouts";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ReactComponent as CreateIcon } from "../../../assets/icon/createIcon.svg";
import { ReactComponent as FilterIcon } from "../../../assets/icon/filterIcon.svg";
import TableAntd from "components/elements/Table";
import { DeleteOutlined, EditOutlined, MoreOutlined } from "@ant-design/icons";
import UpdateTimesheetFilter from "./UpdateTimesheetFilter";
import AddTimesheetForm from "./AddTimesheetForm";
import dayjs from "dayjs"
import { ReactComponent as YesIcon } from "../../../assets/icon/yesCircle.svg";
import { ReactComponent as NoIcon } from "../../../assets/icon/noCircle.svg";
import StatusTag from "components/elements/TagCustom/StatusTag";
import { useState } from "react";

const UpdateMyTimesheet = ({
    activePanel,
    setActivePanel = () => { },
    setEditRecord = () => { },
    TaskOptions = [],
    onUpdateTimesheet = () => { },
    myTimesheetList = [],
    pagination = {},
    isLoading = false,
    onPageChange = () => { },
    editRecord,
    timesheetOptions = [],
    roleOptions = [],
    taskOptions = [],
    setSelectedTimeSheetId = () => { },
    setSelectedRoleId = () => { },
    onApproveTimesheet = () => { },
    handleSearch = () => { },
    onDeleteMyTimesheet = () => { }
}) => {
    const { t, i18n } = useTranslation();
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const hasSelected = selectedRowKeys.length > 0;

    const onSelectChange = newSelectedRowKeys => {
        setSelectedRowKeys(newSelectedRowKeys);
    };

    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
        getCheckboxProps: (record) => ({
            disabled: record?.approveStatusEn?.toLowerCase() !== "draft"
        }),
    };

    const columns = [
        {
            title: <div className="text-table" >{t("my_timesheet.label.dateTimeRecord")}</div>,
            dataIndex: "recordingDate",
            key: "recordingDate",
            width: 120,
            render: (_, record) => (
                dayjs(record?.recordingDate).format(i18n.language == "en" ? "DD/MM/YYYY" : "DD/MM/BBBB")
            )
        },
        {
            title: <div className="text-table" >{t("my_timesheet.label.project")}</div>,
            dataIndex: "projectName",
            key: "projectName",
        },
        {
            title: <div className="text-table" >{t("my_timesheet.label.roles")}</div>,
            dataIndex: "roleName",
            key: "roleName",
        },
        {
            title: <div className="text-table" >{t("my_timesheet.label.task")}</div>,
            dataIndex: "taskName",
            key: "taskName"
        },
        {
            title: <div className="text-table" >{t("my_timesheet.label.number_of_working_hour")}</div>,
            dataIndex: "totalWorkingHours",
            key: "totalWorkingHours",
            align: "center",
            width: 120,
            render: (_, record) => (
                `${record?.totalWorkingHours ?? "0"} ${t("unit.hours")} ${record?.totalWorkingMinutes ?? "0"} ${t("unit.minute_0")}`
            )
        },
        {
            title: <div className="text-table" >{t("my_timesheet.label.details")}</div>,
            dataIndex: "description",
            key: "description",
            width: 150,
        },
        {
            title: <div className="text-table" >{t("my_timesheet.label.billable")}</div>,
            dataIndex: "billableFlag",
            key: "billableFlag",
            align: "center",
            width: 100,
            render: (_, record) => {
                if (record?.billableFlag === "Y") {
                    return <YesIcon />;
                } else if (record?.billableFlag === "N") {
                    return <NoIcon />;
                } else {
                    return null;
                }
            }
        },
        {
            title: <div className="text-table" >{t("my_timesheet.label.status")}</div>,
            dataIndex: "status",
            key: "status",
            align: "center",
            render: (_, record) => {
                return (
                    <StatusTag
                        value={i18n.language == "en" ? record?.approveStatusEn : record?.approveStatus}
                        text={i18n.language == "en" ? record?.approveStatusEn : record?.approveStatus}
                    />
                )
            }
        },
        {
            title: "",
            key: "action",
            align: "center",
            width: 80,
            render: (_, record) => (
                record?.approveStatusEn === "Draft" ? (
                    <Space size="middle">
                        <Dropdown
                            menu={{
                                items: [
                                    {
                                        key: "1",
                                        onClick: () => {
                                            setActivePanel("add");
                                            setEditRecord(record);
                                            setSelectedTimeSheetId(record?.projectId);
                                            setSelectedRoleId(record?.roleId || record?.stpTimeSheetRoleId);
                                            window.scrollTo({ top: 0, behavior: 'smooth' });
                                        },
                                        label: `${t("action.edit")}`,
                                        icon: (
                                            <EditOutlined style={{ fontSize: "18px", color: "gray" }} />
                                        ),
                                    },
                                    {
                                        key: "2",
                                        onClick: () => {
                                            onDeleteMyTimesheet(record)
                                        },
                                        label: `${t("action.delete")}`,
                                        icon: (
                                            <DeleteOutlined style={{ fontSize: "18px", color: "gray" }} />
                                        ),
                                    }
                                ],
                            }}
                        >
                            <MoreOutlined style={{ fontSize: "25px", color: "gray" }} />
                        </Dropdown>
                    </Space>) : null
            ),
        }
    ]

    return (
        <Space direction="vertical" size={24} style={{ width: "100%" }}>
            <Row justify={"end"} style={{ gap: "8px" }}>
                <Button type="default"
                    style={{ height: "40px", borderRadius: "8px", display: "flex", alignItems: "center", color: "#667085" }}
                    icon={<FilterIcon style={{ fontSize: 16 }} />}
                    onClick={() => { setActivePanel(activePanel === "filter" ? null : "filter") }}
                >
                    {t("common.filter")}</Button>
                <Button
                    type="primary"
                    style={{ height: "40px", borderRadius: "8px", display: "flex", alignItems: "center", backgroundColor: "var(--button-color)" }}
                    icon={<CreateIcon style={{ fontSize: 16 }} />}
                    onClick={() => {
                        setEditRecord(null);
                        setActivePanel(activePanel === "add" ? null : "add")
                    }}
                >
                    {t("my_timesheet.label.add_timesheet")}
                </Button>
            </Row>

            {activePanel === "filter" && (
                <Card style={{ border: "solid 1px #EAECF0" }}>
                    <UpdateTimesheetFilter
                        handleSearch={handleSearch}
                        timesheetOptions={timesheetOptions}
                    />
                </Card>
            )}

            {activePanel === "add" && (
                <Card style={{ border: "solid 1pxrgb(36, 38, 43)" }}>
                    <AddTimesheetForm
                        key={editRecord?.txnMyTimeSheetId}
                        TaskOptions={TaskOptions}
                        onUpdateTimesheet={onUpdateTimesheet}
                        initialValues={editRecord}
                        setActivePanel={setActivePanel}
                        timesheetOptions={timesheetOptions}
                        roleOptions={roleOptions}
                        taskOptions={taskOptions}
                        onProjectChange={(value) => setSelectedTimeSheetId(value)}
                        onRoleChange={(value) => setSelectedRoleId(value)}
                        editRecord={editRecord}
                    />
                </Card>
            )}

            <TableAntd
                columns={columns}
                dataSource={myTimesheetList}
                onChange={onPageChange}
                loading={isLoading}
                pagination={{
                    ...pagination,
                }}
                rowSelection={rowSelection}
                rowKey={(record) => record?.txnMyTimeSheetId}
            />

            {hasSelected &&
                (<Flex vertical gap={8} style={{ alignItems: "end" }}>
                    <span>{`${t("common.selected")} ${selectedRowKeys.length} ${t("common.items")}`}</span>
                    <Row style={{ gap: "8px" }}>
                        <Button
                            type="default"
                            style={{ borderRadius: "8px", height: "40px" }}
                            onClick={() => {
                                setSelectedRowKeys([]);
                            }}
                        >
                            {t("my_timesheet.label.cancel")}</Button>
                        <Button
                            type="primary"
                            style={{ borderRadius: "8px", height: "40px", backgroundColor: "var(--button-color" }}
                            onClick={() => {
                                const selectedRecords = myTimesheetList.filter(item =>
                                    selectedRowKeys.includes(item.txnMyTimeSheetId)
                                );
                                onApproveTimesheet(selectedRecords).then(() => {
                                    setSelectedRowKeys([]);
                                });
                            }}
                        >{t("my_timesheet.label.approve")}</Button>
                    </Row>
                </Flex>)}
        </Space>
    )
}

export default UpdateMyTimesheet;